import { type WizardStep } from '@insify/funnel-components';
import { isEmpty } from 'lodash';

import { additionalQuestionKeys, discountQuestionKeys } from '../../../util/activitySpecificQuestions';
import { withOptionalPartnerLogo } from '../../components/ContentWithPartnerLogo';
import { ActivityPage } from '../../pages/Activity/Activity';
import { AdditionalQuestionsPage } from '../../pages/AdditionalQuestions';
import { AlreadyAppliedPage } from '../../pages/AlreadyApplied';
import { CheckoutPage } from '../../pages/Checkout';
import { CompanyPage } from '../../pages/Company';
import { CompanyDetailsPage } from '../../pages/CompanyDetails';
import { ConfirmationPage } from '../../pages/Confirmation';
import { ContactPage } from '../../pages/Contact';
import { CoveragePage } from '../../pages/Coverages';
import { DiscountPage } from '../../pages/Discount';
import { ExclusionsPage } from '../../pages/Exclusions/Exclusions';
import { FoundingDatePage } from '../../pages/FoundingDate';
import { HardDeclinePage } from '../../pages/HardDecline';
import { HeadCountPage } from '../../pages/HeadCount';
import { HighCarePage } from '../../pages/HighCare';
import { HighcareCallbackRequestedPage } from '../../pages/HighcareCallbackRequested';
import { PastClaimsPage } from '../../pages/PastClaims';
import { PastClaimsConfirmPage } from '../../pages/PastClaimsConfirm';
import { PastInsurancePage } from '../../pages/PastInsurance';
import { PropertyDeclarationPage } from '../../pages/PropertyDeclaration';
import { RedirectToConstructionFunnel } from '../../pages/RedirectToConstructionFunnel';
import { RepresentativePage } from '../../pages/Representative';
import { RevenuePage } from '../../pages/Revenue';
import { VersionOutdatedPage } from '../../pages/VersionOutdated';

import type { HighCareConditions } from '../../models/funnel';
import type { Conditions, Params, ProspectStatusResponse } from '../../models/queries';

export type Slug =
  | 'company'
  | 'company-details'
  | 'activity'
  | 'redirect-to-construction-funnel'
  | 'additional-questions'
  | 'headcount'
  | 'revenue'
  | 'discount'
  | 'high-care'
  | 'callback-requested'
  | 'hard-decline'
  | 'founding-date'
  | 'contact'
  | 'coverage'
  | 'past-claims-confirm'
  | 'past-claims'
  | 'past-insurance'
  | 'exclusions'
  | 'representative'
  | 'property-params'
  | 'checkout'
  | 'confirmation'
  | 'version-outdated'
  | 'already-applied';

const companyStep: WizardStep<Slug> = {
  Page: withOptionalPartnerLogo(CompanyPage),
  slug: 'company',
};

const companyDetailsStep: WizardStep<Slug> = {
  Page: withOptionalPartnerLogo(CompanyDetailsPage),
  slug: 'company-details',
};

const activityStep: WizardStep<Slug> = {
  Page: withOptionalPartnerLogo(ActivityPage),
  slug: 'activity',
};

const redirectToConstructionFunnelStep: WizardStep<Slug> = {
  Page: RedirectToConstructionFunnel,
  slug: 'redirect-to-construction-funnel',
};

const additionalQuestionsStep: WizardStep<Slug> = {
  Page: withOptionalPartnerLogo(AdditionalQuestionsPage),
  slug: 'additional-questions',
};

const headCountStep: WizardStep<Slug> = {
  Page: withOptionalPartnerLogo(HeadCountPage),
  slug: 'headcount',
};

const revenueStep: WizardStep<Slug> = {
  Page: withOptionalPartnerLogo(RevenuePage),
  slug: 'revenue',
};

const discountStep: WizardStep<Slug> = {
  Page: withOptionalPartnerLogo(DiscountPage),
  slug: 'discount',
};

const highCareQuoteStep: WizardStep<Slug> = {
  Page: withOptionalPartnerLogo(HighCarePage),
  slug: 'high-care',
};

const callbackRequestedStep: WizardStep<Slug> = {
  Page: withOptionalPartnerLogo(HighcareCallbackRequestedPage),
  slug: 'callback-requested',
};

const hardDeclineQuoteStep: WizardStep<Slug> = {
  Page: withOptionalPartnerLogo(HardDeclinePage),
  slug: 'hard-decline',
};

const foundingDateStep: WizardStep<Slug> = {
  Page: withOptionalPartnerLogo(FoundingDatePage),
  slug: 'founding-date',
};

const contactStep: WizardStep<Slug> = {
  Page: ContactPage,
  slug: 'contact',
};

const coverageStep: WizardStep<Slug> = {
  Page: CoveragePage,
  slug: 'coverage',
};

const pastClaimsConfirmStep: WizardStep<Slug> = {
  Page: withOptionalPartnerLogo(PastClaimsConfirmPage),
  slug: 'past-claims-confirm',
};

const pastClaimsStep: WizardStep<Slug> = {
  Page: withOptionalPartnerLogo(PastClaimsPage),
  slug: 'past-claims',
};

const pastInsuranceStep: WizardStep<Slug> = {
  Page: withOptionalPartnerLogo(PastInsurancePage),
  slug: 'past-insurance',
};

const exclusionsStep: WizardStep<Slug> = {
  Page: withOptionalPartnerLogo(ExclusionsPage),
  slug: 'exclusions',
};

const representativeStep: WizardStep<Slug> = {
  Page: withOptionalPartnerLogo(RepresentativePage),
  slug: 'representative',
};

const propertyParamsStep: WizardStep<Slug> = {
  Page: withOptionalPartnerLogo(PropertyDeclarationPage),
  slug: 'property-params',
};

const highCareBindStep: WizardStep<Slug> = {
  Page: withOptionalPartnerLogo(HighCarePage),
  slug: 'high-care',
};

const checkoutStep: WizardStep<Slug> = {
  Page: withOptionalPartnerLogo(CheckoutPage),
  slug: 'checkout',
};

const confirmationStep: WizardStep<Slug> = {
  Page: ConfirmationPage,
  slug: 'confirmation',
};

const alreadyAppliedStep: WizardStep<Slug> = {
  Page: AlreadyAppliedPage,
  slug: 'already-applied',
};
const versionOutdatedStep: WizardStep<Slug> = {
  Page: VersionOutdatedPage,
  slug: 'version-outdated',
};

const isVersionOutdated = (params?: Partial<Params>, prospectStatus?: Partial<ProspectStatusResponse>) => {
  if (isEmpty(params)) {
    return false;
  }
  return prospectStatus?.versionStatus === 'outdated';
};

const hasPropertyDeclarations = ({
  burglarAlarm,
  fireExtinguishers,
  hardMaterialsBuilding,
  hazards,
  lock,
}: Partial<Conditions['propertyDeclarations']> = {}) => {
  return burglarAlarm || fireExtinguishers || hardMaterialsBuilding || hazards || lock;
};

const hasConfirmedAwarenessRelevantExclusions = (
  quoteParams: Partial<Params>,
  quoteConditions: Partial<Conditions> = {},
) => {
  const isActiveClaimsRelevant = quoteConditions.declarations?.includes('activeClaimAwareness');

  if (isActiveClaimsRelevant) {
    if (quoteParams?.activeClaims !== true) {
      return false;
    }
  }

  const isProductCertificationRelevant =
    quoteConditions?.activitySpecificFieldDescriptions?.find((d) => d.key === 'productCertification') !== undefined;

  // Checks if ProductCertification is relevant and if it is, tests if it is confirmed
  if (isProductCertificationRelevant) {
    if (quoteParams.activitySpecificFieldAnswers?.productCertification !== true) {
      return false;
    }
  }

  const isSegmentSpecificExclusionsRelevant = (quoteConditions?.exclusions?.length ?? 0) > 0;

  // Checks if SegmentSpecificExclusions is relevant and if it is, tests if it is confirmed
  if (isSegmentSpecificExclusionsRelevant) {
    if (quoteParams.segmentSpecificExclusions !== true) {
      return false;
    }
  }

  const knownDeclarations = [
    'hiscoxFrenchCompany',
    'hiscoxNoDoubleInsurance',
    'hiscoxNoPastOrPotentialClaims',
    'hiscoxSecuritySpecific',
    'hiscoxTourismSpecific',
  ];
  const isDeclarationsRelevant = quoteConditions?.declarations?.some((declaration) =>
    knownDeclarations.includes(declaration),
  );
  if (isDeclarationsRelevant) {
    if (
      (quoteParams.declarations?.filter((declaration) => knownDeclarations.includes(declaration)).length ?? 0) === 0
    ) {
      return false;
    }
  }

  return true;
};

const constructionCodes = [
  '43221.a',
  '432200.b',
  '432200.c',
  '4312',
  '4321.a',
  '4331.a',
  '433100.b',
  '4332.c',
  '4333.b',
  '433300.c',
  '4334.a',
  '433400.b',
  '4339.a',
  '42112.a',
  '432900.a',
  '432900.b',
  '432900.c',
  '433200.b',
  '433200.d',
  '433200.i',
  '439930.b',
  '433200.f',
  '433200.g',
  '439900.a',
  '439900.b',
  '439900.c',
  '256200.a',
  '431200.b',
  '433200.e',
  '432200.a',
  '3324',
  '433200.a',
  '4339.b',
  '81221',
  '433200.h',
  '439900.d',
];

const isEveryOccupationConstruction = (params: Partial<Params>) => {
  const { activities = [] } = params;

  if (activities.length === 0) {
    return false;
  }

  return activities.every(({ insifyActivityCode }) => constructionCodes.includes(insifyActivityCode));
};

export type GetStepsArg = {
  quoteParams?: Partial<Params>;
  quoteConditions?: Partial<Conditions>;
  highCareConditions?: Partial<HighCareConditions>;
  isEnteringCompanyDetails?: boolean;
  hasRequestedHighCare?: boolean;
  prospectStatus?: Partial<ProspectStatusResponse>;
  hasSubmittedApplication?: boolean;
  showContactStep?: boolean;
};

export const getSteps = ({
  quoteParams,
  quoteConditions,
  highCareConditions,
  isEnteringCompanyDetails,
  hasRequestedHighCare,
  prospectStatus,
  hasSubmittedApplication,
  showContactStep,
}: GetStepsArg): WizardStep<Slug>[] => {
  if (prospectStatus?.orderStatus === 'applied') {
    return [alreadyAppliedStep];
  }

  if (isVersionOutdated(quoteParams, prospectStatus)) {
    return [versionOutdatedStep];
  }

  let steps = [activityStep];

  if (!quoteParams?.activities?.length) {
    return steps;
  }

  if (isEveryOccupationConstruction(quoteParams)) {
    return [...steps, redirectToConstructionFunnelStep];
  }

  const additionalQuestions =
    quoteConditions?.activitySpecificFieldDescriptions?.filter(({ key }) =>
      // When the type of additionalQuestionKeys is const, TS expects the `key` is the same, which is not the purpose of this filter.
      (additionalQuestionKeys as string[]).includes(key),
    ) ?? [];

  if (additionalQuestions.length !== 0) {
    steps = [...steps, additionalQuestionsStep];
    if (!additionalQuestions.some(({ key }) => key in (quoteParams.activitySpecificFieldAnswers || {}))) {
      return steps;
    }
  }

  if (!quoteConditions?.skipSteps?.includes('headcount')) {
    steps = [...steps, headCountStep];

    if (quoteParams?.headCount === undefined) {
      return steps;
    }
  }

  steps = [...steps, revenueStep];

  if (quoteParams?.annualRevenue === undefined) {
    return steps;
  }

  const discountQuestions =
    quoteConditions?.activitySpecificFieldDescriptions?.filter(({ key }) =>
      // When the type of discountQuestionKeys is const, TS expects the `key` is the same, which is not the purpose of this filter.
      (discountQuestionKeys as string[]).includes(key),
    ) ?? [];

  if (discountQuestions.length !== 0) {
    steps = [...steps, discountStep];
    if (!discountQuestions.some(({ key }) => key in (quoteParams.activitySpecificFieldAnswers || {}))) {
      return steps;
    }
  }

  if (
    highCareConditions?.highCareHardDecline &&
    !checkHighCareReasonHasPastClaims(highCareConditions) &&
    !checkHighCareReasonPostalCodeOnly(highCareConditions)
  ) {
    return [...steps, hardDeclineQuoteStep];
  }

  if (highCareConditions?.highCareQuoteWizard) {
    steps = [...steps, highCareQuoteStep];

    if (hasRequestedHighCare) {
      steps = [...steps, callbackRequestedStep];
    }
    return steps;
  }

  steps = [...steps, foundingDateStep];

  if (quoteParams.companyRegistrationStatus === undefined) {
    return steps;
  }

  if (showContactStep) {
    steps = [...steps, contactStep];

    if (!quoteParams.representative?.email || !quoteParams.representative.phone) {
      return steps;
    }
  }

  steps = [...steps, coverageStep, pastClaimsConfirmStep];

  if (quoteParams.pastClaimsConfirmed === undefined) {
    return steps;
  }

  if (highCareConditions?.highCareHardDecline && !checkHighCareReasonPostalCodeOnly(highCareConditions)) {
    return [...steps, hardDeclineQuoteStep];
  }

  if (quoteParams.pastClaimsConfirmed && !quoteConditions?.skipSteps?.includes('past-claims')) {
    steps = [...steps, pastClaimsStep];

    if (quoteParams.pastClaimsSumBelowThresholdConfirmed !== true) {
      if (quoteParams.pastClaims === undefined || quoteParams.pastClaims.length === 0) {
        return steps;
      }
    }
  }

  if (!quoteConditions?.skipSteps?.includes('past-insurance')) {
    steps = [...steps, pastInsuranceStep];

    if (quoteParams.pastInsuranceIssues === undefined) {
      return steps;
    }
  }

  steps = [...steps, exclusionsStep];

  if (!hasConfirmedAwarenessRelevantExclusions(quoteParams, quoteConditions)) {
    return steps;
  }

  if (hasPropertyDeclarations(quoteConditions?.propertyDeclarations)) {
    steps = [...steps, propertyParamsStep];
  }

  if (
    hasPropertyDeclarations(quoteConditions?.propertyDeclarations) &&
    quoteParams.propertyDeclarations === undefined
  ) {
    return steps;
  }

  if (quoteParams.companyRegistrationStatus !== 'not-registered') {
    steps.push(companyStep);

    if (isEnteringCompanyDetails || quoteParams?.requestCompanyDetails) {
      steps = [...steps, companyDetailsStep];
    }

    if (!quoteParams?.companyRegistrationStatus || !quoteParams.companyName) {
      return steps;
    }
  }

  steps = [...steps, representativeStep];

  if (!quoteParams.address || (quoteParams.representative?.email ?? '') === '') {
    return steps;
  }

  if (highCareConditions?.highCareHardDecline) {
    return [...steps, hardDeclineQuoteStep];
  }

  if (highCareConditions?.highCareBindWizard) {
    steps = [...steps, highCareBindStep];

    if (hasRequestedHighCare) {
      steps = [...steps, callbackRequestedStep];
    }
    return steps;
  }

  steps = [...steps, checkoutStep];
  if (!hasSubmittedApplication) {
    return steps;
  }

  return [...steps, confirmationStep];
};

/**
 * @todo(https://insify.atlassian.net/browse/IDF-248): in reject reasons add not only messages but the reject code.
 * Handling a code (no relying on a message) seems a more robust way.
 */
export enum HIGH_CARE_REASON {
  POSTAL_CODE = 'postal code is not supported',
  PAST_CLAIMS = 'has past claims issues',
}

const checkHighCareReasonPostalCodeOnly = (highCareConditions?: Partial<HighCareConditions>) =>
  highCareConditions?.highCareReason?.every(({ message }) => message.includes(HIGH_CARE_REASON.POSTAL_CODE));

const checkHighCareReasonHasPastClaims = (highCareConditions?: Partial<HighCareConditions>) =>
  highCareConditions?.highCareReason?.some(({ message }) => message === HIGH_CARE_REASON.PAST_CLAIMS);
